import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "../Reveal";
import PreviewHero from "./PreviewHero";
import VideoModal from "./VideoModal";
import workshop from "../../assets/workshop.webp";
import chimpChampChief from "../../assets/chimpchampchief.png";

const idaQuote =
  "Kæmpe anbefaling! Noget af det, jeg især værdsatte ved Stines tilgang, var, at hun gjorde noget, der for mig virkede uoverskueligt, helt konkret og enkelt, hvilket gjorde processen motiverende. For mig kom det især til at handle om mine tankemønstre og vaner. Stine hjalp mig med at se tanker og vaner som noget, jeg ikke bare behøver at reagere på, men som jeg selv kan forholde mig til og vælge, hvordan jeg vil handle på. Det har givet mig en helt anden bevidsthed om mine egne reaktioner og handlemuligheder. Det er nok den bedste anbefaling, jeg kan give: Det, jeg lærte hos Stine, er ikke bare noget, jeg brugte under forløbet, men noget jeg stadig aktivt bruger i mit arbejdsliv og min hverdag.";

const devQuote =
  "I had the privilege of inviting Stine and Julie to lead the \u201cEmpower: Teachers Training Teachers\u201d workshop at Ensana Hotel and Spa in Budapest. Their work on voice as a powerful tool for teaching completely transformed our group. Through practical techniques on energy, volume control, self-confidence, and motivation, they showed us how to speak with presence, hold a room, and lead from authenticity. Stine and Julie created a safe, energizing space where every teacher left feeling clearer, louder, and more confident in their voice — both on and off the mat. For any teacher who wants to elevate their communication and impact, this workshop is essential. Thank you for bringing such power and heart to Budapest.";

const BILLING_VIDEO = { src: "/media/billing-udenfor-banen.mp4", poster: "/media/billing-udenfor-banen-poster.jpg" };

const Arrow = ({ className = "text-[#852936]" }) => (
  <span
    className={`flex-shrink-0 text-xl leading-none transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 ${className}`}
    aria-hidden="true"
  >
    ↗
  </span>
);

const PanelLabel = ({ title, small }) => (
  <div className="flex min-h-[84px] items-start justify-between gap-3 border-b border-black/[0.08] bg-white px-5 py-4">
    <h3 className={`font-display font-semibold leading-snug text-[#111827] ${small ? "text-[14px]" : "text-[17px]"}`}>
      {title}
    </h3>
    <Arrow />
  </div>
);

const Quote = ({ text, name, context }) => (
  <div className="flex flex-1 flex-col justify-between bg-white p-5">
    <div>
      <span className="font-display text-4xl leading-none text-[#852936]/35" aria-hidden="true">
        &ldquo;
      </span>
      <p className="-mt-1 text-[13px] italic leading-relaxed text-[#111827]/75">{text}</p>
    </div>
    <div className="mt-5">
      <p className="text-[15px] font-semibold text-[#111827]">{name}</p>
      <p className="text-[13px] text-[#111827]/55">{context}</p>
    </div>
  </div>
);

const panelBase = "group flex flex-col border-b border-black/[0.08] text-left sm:border-r";

const PreviewForside = () => {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <>
      <PreviewHero />
      <section className="bg-white" data-testid="preview-forside-sections">
        <Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {/* 1. Coaching */}
            <Link to="/coaching" className={`${panelBase} min-h-[380px] bg-[#F4F1EC] lg:min-h-[540px]`} data-testid="preview-forside-panel-coaching">
              <div className="flex justify-end px-5 py-4">
                <Arrow />
              </div>
              <div className="flex flex-1 flex-col justify-end p-5">
                <p className="font-display text-[34px] font-bold leading-[1.05] tracking-tight text-[#852936] xl:text-[38px]">
                  Coaching.
                  <br />
                  Mentaltræning.
                  <br />
                  Mentorforløb.
                </p>
              </div>
            </Link>

            {/* 2. Philip Billing – åbner video med lyd */}
            <button
              type="button"
              onClick={() => setVideoOpen(true)}
              className={`${panelBase} lg:border-r`}
              data-testid="preview-forside-panel-billing"
            >
              <PanelLabel title={<>En snak med<br />Philip Billing</>} />
              <div className="relative h-[380px] w-full flex-1 overflow-hidden bg-black lg:h-[456px] lg:flex-none">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster={BILLING_VIDEO.poster}
                  className="h-full w-full object-cover"
                  style={{ objectPosition: "center 22%" }}
                  data-testid="preview-forside-billing-video"
                >
                  <source src={BILLING_VIDEO.src} type="video/mp4" />
                </video>
                <span className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-[13px] font-semibold text-[#111827] transition-transform duration-300 group-hover:scale-[1.04]">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5"><path d="M8 5v14l11-7z" /></svg>
                  Se med lyd
                </span>
              </div>
            </button>

            {/* 3. Statement → Om mig */}
            <Link to="/om" className={`${panelBase} min-h-[380px] bg-[#852936] lg:min-h-0`} data-testid="preview-forside-panel-om">
              <div className="flex justify-end px-5 py-4">
                <Arrow className="text-white" />
              </div>
              <div className="flex flex-1 flex-col justify-center p-5">
                <p className="font-display text-[26px] font-semibold leading-[1.15] tracking-tight text-white">
                  Større indre ro.
                  <br />
                  <br />
                  Mere mental frihed.
                  <br />
                  <br />
                  Masser af mod.
                </p>
              </div>
            </Link>

            {/* 4. Referencer – Ida */}
            <Link to="/referencer" className={`${panelBase} sm:border-r-0`} data-testid="preview-forside-panel-referencer">
              <PanelLabel title="Referencer" />
              <Quote text={idaQuote} name="Ida Ytterberg" context="Individuelt coachingforløb" />
            </Link>

            {/* 5. Undervisning på rejsehøjskole */}
            <Link to="/undervisning" className={panelBase} data-testid="preview-forside-panel-undervisning">
              <PanelLabel title="Undervisning i personlig udvikling på rejsehøjskole" />
              <div className="h-[380px] flex-1 overflow-hidden lg:h-[456px] lg:flex-none">
                <img
                  src={workshop}
                  alt="Stine underviser på rejsehøjskole"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </div>
            </Link>

            {/* 6. Referencer – Dev */}
            <Link to="/referencer" className={`${panelBase} lg:border-r`} data-testid="preview-forside-panel-referencer-dev">
              <PanelLabel title="Referencer" />
              <Quote text={devQuote} name="Dev Kapil" context="Founder, Progressive Hatha Yoga School" />
            </Link>

            {/* 7. Undervisning & Workshops */}
            <Link to="/undervisning" className={`${panelBase} min-h-[380px] bg-[#F4F1EC] lg:min-h-0`} data-testid="preview-forside-panel-workshops">
              <div className="flex justify-end px-5 py-4">
                <Arrow />
              </div>
              <div className="flex flex-1 flex-col justify-end p-5">
                <p className="font-display text-[21px] font-semibold leading-tight tracking-tight text-[#111827]">
                  Undervisning &amp; Workshops
                </p>
                <p className="mt-5 font-display text-[25px] font-bold leading-[1.15] tracking-tight text-[#852936] xl:text-[27px]">
                  Forstå dig selv.
                  <br />
                  Vær dig selv.
                  <br />
                  Vær med andre.
                  <br />
                  Udtryk dig selv.
                </p>
              </div>
            </Link>

            {/* 8. doubleyou */}
            <Link to="/perspektiver" className={`${panelBase} sm:border-r-0`} data-testid="preview-forside-panel-doubleyou">
              <PanelLabel
                small
                title="Lær hjernens to mentale systemer at kende – der, hvor der opstår en indre kamp, når der er noget på spil. Mød doubleyou's chimp, champ og chief."
              />
              <div className="flex flex-1 items-center justify-center bg-white p-6">
                <img src={chimpChampChief} alt="doubleyou's chimp, champ og chief" className="max-h-[320px] w-full object-contain" />
              </div>
            </Link>
          </div>
        </Reveal>
      </section>

      {videoOpen && <VideoModal src={BILLING_VIDEO.src} poster={BILLING_VIDEO.poster} onClose={() => setVideoOpen(false)} />}
    </>
  );
};

export default PreviewForside;
